import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from "react";
import { supabase } from "@/integrations/supabase/client";
import { User } from "@supabase/supabase-js";
import { useShippingSettings } from "@/hooks/useShippingSettings";

export interface CartItem {
  id: string;
  name: string;
  price: number;
  image: string;
  size: string;
  quantity: number;
}

const CART_STORAGE_KEY = "shopping_cart";

type CartListener = () => void;

const loadCartFromStorage = (): CartItem[] => {
  try {
    const stored = localStorage.getItem(CART_STORAGE_KEY);
    return stored ? (JSON.parse(stored) as CartItem[]) : [];
  } catch {
    return [];
  }
};

const saveCartToStorage = (items: CartItem[]) => {
  localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
};

// A tiny in-memory store so multiple components stay in sync
const cartStore = (() => {
  let items: CartItem[] = loadCartFromStorage();
  let currentUser: User | null = null;
  let isSyncing = false;
  const listeners = new Set<CartListener>();

  const emit = () => {
    listeners.forEach((l) => l());
  };

  const setItems = (next: CartItem[], skipDbSync = false) => {
    items = next;
    saveCartToStorage(items);
    emit();

    // Sync to database if user is logged in
    if (currentUser && !skipDbSync && !isSyncing) {
      syncToDatabase(currentUser.id, next);
    }
  };

  const syncToDatabase = async (userId: string, cartItems: CartItem[]) => {
    try {
      // Delete all existing cart items for user
      await supabase.from("user_carts").delete().eq("user_id", userId);

      // Insert current cart items
      if (cartItems.length > 0) {
        const rows = cartItems.map((item) => ({
          user_id: userId,
          product_id: item.id,
          product_name: item.name,
          price: Math.round(item.price * 100), // Store in cents
          image: item.image,
          size: item.size,
          quantity: item.quantity,
        }));

        await supabase.from("user_carts").insert(rows);
      }
    } catch (error) {
      console.error("Failed to sync cart to database:", error);
    }
  };

  const loadFromDatabase = async (userId: string) => {
    try {
      isSyncing = true;
      const { data, error } = await supabase
        .from("user_carts")
        .select("*")
        .eq("user_id", userId);

      if (error) throw error;

      if (data && data.length > 0) {
        const dbItems: CartItem[] = data.map((row) => ({
          id: row.product_id,
          name: row.product_name,
          price: row.price / 100, // Convert from cents
          image: row.image,
          size: row.size,
          quantity: row.quantity,
        }));

        // Merge with local cart (local items take precedence for same product+size)
        const localItems = loadCartFromStorage();
        const merged = [...dbItems];

        for (const localItem of localItems) {
          const existingIndex = merged.findIndex(
            (m) => m.id === localItem.id && m.size === localItem.size
          );
          if (existingIndex >= 0) {
            // Local takes precedence - use local quantity instead of adding
            merged[existingIndex].quantity = localItem.quantity;
          } else {
            merged.push(localItem);
          }
        }

        items = merged;
        saveCartToStorage(merged);
        emit();

        // Sync merged cart back to database
        await syncToDatabase(userId, merged);
      } else if (loadCartFromStorage().length > 0) {
        // No items in DB but we have local items - sync them up
        await syncToDatabase(userId, items);
      }
    } catch (error) {
      console.error("Failed to load cart from database:", error);
    } finally {
      isSyncing = false;
    }
  };

  const setUser = (user: User | null) => {
    const wasLoggedOut = !currentUser && user;
    currentUser = user;

    if (wasLoggedOut && user) {
      // User just logged in - load and merge cart from database
      loadFromDatabase(user.id);
    }
  };

  const subscribe = (listener: CartListener) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
  };

  const getSnapshot = () => items;

  const addItem = (item: Omit<CartItem, "quantity">) => {
    const existing = items.find((i) => i.id === item.id && i.size === item.size);
    const next = existing
      ? items.map((i) =>
          i.id === item.id && i.size === item.size
            ? { ...i, quantity: i.quantity + 1 }
            : i
        )
      : [...items, { ...item, quantity: 1 }];

    setItems(next);
  };

  const updateQuantity = (id: string, size: string, delta: number) => {
    const next = items.map((i) =>
      i.id === id && i.size === size
        ? { ...i, quantity: Math.max(1, i.quantity + delta) }
        : i
    );
    setItems(next);
  };

  const removeItem = (id: string, size: string) => {
    const next = items.filter((i) => !(i.id === id && i.size === size));
    setItems(next);
  };

  const clearCart = () => {
    items = [];
    localStorage.removeItem(CART_STORAGE_KEY);
    emit();

    // Also clear from database if logged in
    if (currentUser) {
      supabase.from("user_carts").delete().eq("user_id", currentUser.id);
    }
  };

  // Keep in sync across tabs/windows
  if (typeof window !== "undefined") {
    window.addEventListener("storage", (e) => {
      if (e.key !== CART_STORAGE_KEY) return;
      items = loadCartFromStorage();
      emit();
    });
  }

  return {
    subscribe,
    getSnapshot,
    addItem,
    updateQuantity,
    removeItem,
    clearCart,
    setUser,
  };
})();

export const useCart = () => {
  const cartItems = useSyncExternalStore(cartStore.subscribe, cartStore.getSnapshot, cartStore.getSnapshot);
  const [isLoading, setIsLoading] = useState(true);
  const shippingSettings = useShippingSettings();

  // Listen for auth changes to sync cart
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        cartStore.setUser(session?.user ?? null);
        setIsLoading(false);
      }
    );

    // Check initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      cartStore.setUser(session?.user ?? null);
      setIsLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const addItem = useCallback((item: Omit<CartItem, "quantity">) => {
    cartStore.addItem(item);
  }, []);

  const updateQuantity = useCallback((id: string, size: string, delta: number) => {
    cartStore.updateQuantity(id, size, delta);
  }, []);

  const removeItem = useCallback((id: string, size: string) => {
    cartStore.removeItem(id, size);
  }, []);

  const clearCart = useCallback(() => {
    cartStore.clearCart();
  }, []);

  const { subtotal, shipping, total, itemCount } = useMemo(() => {
    const subtotalValue = cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
    const shippingValue = subtotalValue > shippingSettings.threshold ? 0 : shippingSettings.cost;
    const totalValue = subtotalValue + shippingValue;
    const itemCountValue = cartItems.reduce((sum, item) => sum + item.quantity, 0);

    return {
      subtotal: subtotalValue,
      shipping: shippingValue,
      total: totalValue,
      itemCount: itemCountValue,
    };
  }, [cartItems, shippingSettings]);

  return {
    cartItems,
    addItem,
    updateQuantity,
    removeItem,
    clearCart,
    subtotal,
    shipping,
    total,
    itemCount,
    isLoading,
  };
};
